import React, { FC, useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { FormGroup, Label, Input, FormFeedback, Button, Alert, Spinner } from "reactstrap"
import { ApplicationState } from "../../../core/types"
import { User } from "../../../data/model/user-model"
import { apiCache } from "../../../data/services/api-cache"
import { registerSystem } from "../../../data/api"
import { summarizeErrorMessage } from "../../../core/utils/error-message"

const SUPPORT_EMAIL = "support@pacb.com"

// A basic RFC-5322-ish sanity check. The backend is the authoritative validator;
// this only catches obvious typos before submit so the admin gets inline feedback.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FormState {
    firstName: string
    lastName: string
    companyName: string
    role: string
    email: string
    systemId: string
}

const TEXT_FIELDS: { name: keyof FormState; label: string; placeholder: string }[] = [
    { name: "firstName",   label: "First Name",   placeholder: "Jane" },
    { name: "lastName",    label: "Last Name",    placeholder: "Smith" },
    { name: "companyName", label: "Company Name", placeholder: "Acme Genomics" },
    { name: "role",        label: "Role",         placeholder: "Lab Manager" },
]

type Status = "idle" | "loading" | "success" | "error" | "alreadyRegistered"

interface Props {
    // Called once the server accepts the registration (verification email sent).
    onRegistered?: () => void
}

export const RegistrationForm: FC<Props> = ({ onRegistered }) => {
    const user = useSelector<ApplicationState, User>(state => state.auth.user)

    const [form, setForm] = useState<FormState>({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        companyName: "",
        role: "",
        email: user?.email || "",
        systemId: "",
    })
    const [status, setStatus] = useState<Status>("idle")
    const [errorMsg, setErrorMsg] = useState("")
    const [emailError, setEmailError] = useState("")
    const [submittedEmail, setSubmittedEmail] = useState("")

    useEffect(() => {
        apiCache.getSlStatus().then(slStatus => {
            if (slStatus?.uuid) {
                setForm(prev => ({ ...prev, systemId: prev.systemId || slStatus.uuid }))
            }
        }).catch(() => {})
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        if (name === "email") {
            setEmailError("")
        }
    }

    const validateEmail = (): boolean => {
        if (!form.email.trim()) {
            setEmailError("Email address is required.")
            return false
        }
        if (!EMAIL_PATTERN.test(form.email.trim())) {
            setEmailError("Enter a valid email address.")
            return false
        }
        setEmailError("")
        return true
    }

    const optional = (value: string) => {
        const trimmed = value.trim()
        return trimmed === "" ? undefined : trimmed
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validateEmail()) {
            return
        }
        const email = form.email.trim()
        setStatus("loading")
        setErrorMsg("")
        try {
            // Routes through the SL backend (never the cloud service directly),
            // which performs the cloud registration and stores the credentials.
            await registerSystem({
                email,
                systemId: form.systemId,
                firstName: optional(form.firstName),
                lastName: optional(form.lastName),
                companyName: optional(form.companyName),
                role: optional(form.role),
            })
            setSubmittedEmail(email)
            setStatus("success")
            onRegistered?.()
        } catch (err: any) {
            if (err?.status === 409) {
                // The system is already registered to a different account.
                setStatus("alreadyRegistered")
            } else {
                setStatus("error")
                setErrorMsg(summarizeErrorMessage(err?.data?.message) || "Registration failed. Please try again.")
            }
        }
    }

    if (status === "success") {
        return (
            <Alert color="success" className="mb-0">
                Verification email sent to <strong>{submittedEmail}</strong>. Check your inbox.
            </Alert>
        )
    }

    if (status === "alreadyRegistered") {
        return (
            <Alert color="warning" className="mb-0">
                This SMRT Link system is already registered to a different account. Please contact PacBio
                support at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for assistance.
            </Alert>
        )
    }

    const disabled = status === "loading"
    // The required fields (per the PRD): email and the auto-detected system ID.
    const requiredComplete = form.email.trim() !== "" && form.systemId.trim() !== ""

    return (
        <form onSubmit={handleSubmit} noValidate>
            {TEXT_FIELDS.map(({ name, label, placeholder }) => (
                <FormGroup key={name}>
                    <Label for={name}>
                        {label}
                        <span className="text-muted ml-1 small">(optional)</span>
                    </Label>
                    <Input
                        id={name}
                        name={name}
                        type="text"
                        placeholder={placeholder}
                        value={form[name]}
                        onChange={handleChange}
                        disabled={disabled}
                    />
                </FormGroup>
            ))}

            <FormGroup>
                <Label for="email">Email Address</Label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane.smith@example.com"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={() => form.email.trim() && validateEmail()}
                    required
                    disabled={disabled}
                    invalid={!!emailError}
                />
                <FormFeedback>{emailError}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="systemId">
                    SMRT Link System ID
                    <span className="text-muted ml-1 small">(auto-detected)</span>
                </Label>
                <Input
                    id="systemId"
                    name="systemId"
                    type="text"
                    value={form.systemId}
                    readOnly
                    disabled={disabled}
                    style={{ backgroundColor: "#f5f5f5", cursor: "default" }}
                />
            </FormGroup>

            {status === "error" && <Alert color="danger" style={{ overflowWrap: "anywhere" }}>{errorMsg}</Alert>}

            <Button type="submit" color="primary" disabled={disabled || !requiredComplete}>
                {status === "loading"
                    ? <><Spinner size="sm" className="mr-2" />Registering…</>
                    : "Register"}
            </Button>
        </form>
    )
}
